<!-- Main search toolbar -->
<mat-toolbar class="search-toolbar">
  <mat-toolbar-row class="gap-2">
    <!-- Search input area with Material styling -->
    <div class="search-input-area" cdkOverlayOrigin #overlayOrigin="cdkOverlayOrigin">
      <!-- Search icon prefix -->
      <mat-icon class="search-prefix-icon">search</mat-icon>

      <!-- Search input -->
      <input
        #searchInput
        class="search-input"
        [formControl]="queryForm!"
        [placeholder]="currentPlaceholder"
        role="combobox"
        [attr.aria-expanded]="showAutocompleteOverlay"
        aria-haspopup="listbox"
        aria-autocomplete="list"
        (focus)="onFocus()"
        (blur)="onBlur()"
        (keydown)="onKeyDown($event)"
        autocomplete="off" />

      <!-- Clear button -->
      <div matTooltip="Clear search">
        <button
          matIconButton
          class="icon-mini-fab ml-2"
          [style.visibility]="hasSearchValue ? 'visible' : 'hidden'"
          [disabled]="isSearchDisabled$ | async"
          (click)="clearSearch()">
          <mat-icon>close</mat-icon>
        </button>
      </div>
      <mat-divider vertical></mat-divider>

      <!-- Search Type Mode buttons (radio group behavior) — disabled without a valid geofencing area -->
      <div class="d-flex">
        <div [matTooltip]="textSearchTooltip$ | async">
          <button
            matIconButton
            class="icon-mini-fab mat-icon-toggle-button"
            [class.selected]="(searchQueryService.searchTypeMode$ | async) === SearchTypeMode.TEXT_SEARCH && !(isTextSearchDisabled$ | async)"
            [disabled]="isTextSearchDisabled$ | async"
            (click)="changeSearchMode(SearchTypeMode.TEXT_SEARCH)">
            <mat-icon>keyboard</mat-icon>
          </button>
        </div>

        <div [matTooltip]="locationSearchTooltip$ | async">
          <button
                  matIconButton
            class="icon-mini-fab mat-icon-toggle-button"
            [class.selected]="(searchQueryService.searchTypeMode$ | async) === SearchTypeMode.LOCATION_SEARCH && !(isLocationSearchDisabled$ | async)"
            [disabled]="isLocationSearchDisabled$ | async"
            (click)="changeSearchMode(SearchTypeMode.LOCATION_SEARCH)">
            <mat-icon>add_location_alt</mat-icon>
          </button>
        </div>

        <div [matTooltip]="imageSearchTooltip$ | async">
          <button
                  matIconButton
            class="icon-mini-fab mat-icon-toggle-button"
            [class.selected]="(searchQueryService.searchTypeMode$ | async) === SearchTypeMode.IMAGE_SEARCH && !(isImageSearchDisabled$ | async)"
            [disabled]="isImageSearchDisabled$ | async"
            (click)="changeSearchMode(SearchTypeMode.IMAGE_SEARCH)">
            <mat-icon>camera_alt</mat-icon>
          </button>
        </div>
      </div>
    </div>

    <ng-container *ngIf="isHandset$ | async; else desktopContainer">
      <button matIconButton [matMenuTriggerFor]="handsetMenu" matTooltip="Options">
        <mat-icon>settings</mat-icon>
      </button>
      <mat-menu #handsetMenu="matMenu">
        <button mat-menu-item (click)="openGeofencingDialog()">
          <mat-icon>crop_free</mat-icon>
          <span>{{ geofencingButtonTooltip$ | async }}</span>
        </button>
        <mat-divider></mat-divider>
        <button
          mat-menu-item
          [class.active]="(searchQueryService.searchDisplayMode$ | async) === SearchDisplayMode.GRID"
          (click)="onViewChange(SearchDisplayMode.GRID)">
          <mat-icon>grid_view</mat-icon>
          Gallery View
        </button>
        <button
          mat-menu-item
          [class.active]="(searchQueryService.searchDisplayMode$ | async) === SearchDisplayMode.MAP"
          (click)="onViewChange(SearchDisplayMode.MAP)">
          <mat-icon>map</mat-icon>
          Map View
        </button>
      </mat-menu>
    </ng-container>
    <ng-template #desktopContainer>
      <!-- Search button — a valid geofencing area is mandatory for the embeddings API -->
      <div [matTooltip]="searchButtonTooltip$ | async">
        <button
          matButton="tonal"
          class="search-button"
          (click)="onSearchExecute()"
          [disabled]="!hasSearchValue || isLoading || (isSearchDisabled$ | async) || !(geofencingValidation$ | async)?.valid">
          <mat-icon *ngIf="isLoading" class="loading-icon">refresh</mat-icon>
          Search
        </button>
      </div>
      <!-- Geofencing toggle button -->
      <button matIconButton
              [class.active]="(geofencingValidation$ | async)?.valid"
              [class.geofencing-invalid]="!(geofencingValidation$ | async)?.valid"
              (click)="openGeofencingDialog()" [matTooltip]="geofencingButtonTooltip$ | async">
        <mat-icon>crop_free</mat-icon>
      </button>
      <mat-divider vertical></mat-divider>

      <!-- View display toggle group -->
      <button
        matIconButton
        [class.active]="(searchQueryService.searchDisplayMode$ | async) === SearchDisplayMode.GRID"
        (click)="onViewChange(SearchDisplayMode.GRID)">
        <mat-icon>grid_view</mat-icon>
      </button>
      <button
        matIconButton
        [class.active]="(searchQueryService.searchDisplayMode$ | async) === SearchDisplayMode.MAP"
        (click)="onViewChange(SearchDisplayMode.MAP)">
        <mat-icon>map</mat-icon>
      </button>

      <div class="spacer"></div>
        @if ((featureFlagService.getFlags$() | async)?.[FeatureFlags.AI_AGENTS]){
            <!-- AI view toggle button -->
            <button
                    matIconButton
                    [class.active]="(searchQueryService.searchDisplayMode$ | async) === SearchDisplayMode.AI"
                    (click)="onViewChange(SearchDisplayMode.AI)"
                    matTooltip="AI Assistant">
                <mat-icon>smart_toy</mat-icon>
            </button>
        }


      @if ((featureFlagService.getFlags$() | async)?.[FeatureFlags.AI_AGENTS]){
        <!-- Property Analyst view toggle button -->
        <button
          matIconButton
          [class.active]="(searchQueryService.searchDisplayMode$ | async) === SearchDisplayMode.PROPERTY"
          (click)="onViewChange(SearchDisplayMode.PROPERTY)"
          matTooltip="Property Analyst">
          <mat-icon>house</mat-icon>
        </button>
      }

    </ng-template>
  </mat-toolbar-row>
</mat-toolbar>

<!-- Autocomplete overlay -->
<ng-template
  cdkConnectedOverlay
  [cdkConnectedOverlayOrigin]="overlayOrigin"
  [cdkConnectedOverlayOpen]="showAutocompleteOverlay"
  [cdkConnectedOverlayPositions]="[
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 2
    }
  ]"
  [cdkConnectedOverlayMinWidth]="500"
  [cdkConnectedOverlayHasBackdrop]="false">
  <div class="autocomplete-overlay w-100" role="listbox" aria-label="Search suggestions">
    <div class="autocomplete-header">
      <span>Suggestions ({{ visibleSuggestions.length }})</span>
    </div>
    @if (!(geofencingValidation$ | async)?.valid) {
      <div class="autocomplete-geofencing-warning d-flex align-items-center gap-2 p-2 px-3 border-bottom small">
        <mat-icon>crop_free</mat-icon>
        <span>{{ (geofencingValidation$ | async)?.reason }}</span>
      </div>
    }
    <app-suggestion-panel
      *ngFor="let suggestion of visibleSuggestions; let i = index; trackBy: trackBySuggestion"
      [suggestion]="suggestion"
      [isActive]="activeSuggestionIndex === i"
      (suggestionClick)="onSuggestionSelect($event)"></app-suggestion-panel>
  </div>
</ng-template>
